import style from './PickupPage.css';
import Title from '../../components/shared/Typography/Title';
import useTranslation from '../../localization/i18n.context';
import LocationPin from '../../assets/images/location-pin.webp';
import List from '../../components/shared/List/List';
import ListItem from '../../components/shared/List/ListItem';
import BoxIcon from '../../components/shared/Icons/BoxIcon';
import SwitchIcon from '../../components/shared/Icons/SwitchIcon';
import Popup from '../../components/shared/Popup/Popup';
import { PermissionsEvent } from '../../sdk/permissions.event';
import { renderIf } from '../../utils/render.utils';
import LoadingScreen from '../../components/shared/LoadingScreen/LoadingScreen';
import NavigationUtils from '../../utils/navigation.utils';
import { useState } from 'preact/hooks';
import { PickupType } from './PickupProvider';
import usePermissionCheck from '../../hooks/permission.listener.hook';
import Alert from '../../components/shared/Alert/Alert';

export default function PickupView({
  ownPickups = false,
  showPopup = false,
  organizationCode,
  type,
}) {
  const [loadingScreen, setLoadingScreen] = useState(false);

  const showPickupLocations = () => {
    setLoadingScreen(true);
    PermissionsEvent.check(
      PermissionsEvent.Resources.LOCATION,
      PermissionsEvent.Action.ASK,
    );
  };

  const startActivationFlow = () => {
    NavigationUtils.navigate(NavigationUtils.Routes.CARD_ACTIVATION);
  };

  const onPermissionResponse = (data, times) => {
    const { status, location } = data;
    if (
      status === PermissionsEvent.Status.ASK_AGAIN &&
      times < PermissionsEvent.PermissionMaxRequestTimes
    ) {
      return PermissionsEvent.check(
        PermissionsEvent.Resources.LOCATION,
        PermissionsEvent.Action.ASK,
      );
    }

    if (status === PermissionsEvent.Status.GRANTED && location != null) {
      return NavigationUtils.navigate(
        NavigationUtils.Routes.PICKUP_DETAIL,
        window.location.search.includes('popup'),
        { lat: location.latitude, long: location.longitude, type: type },
      );
    }

    return NavigationUtils.navigate(NavigationUtils.Routes.PICKUP, false, {
      popup: true,
      type: type,
    });
  };

  usePermissionCheck(onPermissionResponse, PermissionsEvent.Resources.LOCATION);

  const { t, getOrganization } = useTranslation();

  return (
    <main className={style.main}>
      <Title text={t('What would you like to do?')} />
      {renderIf(
        ![PickupType.CREDIT].includes(type),
        <>
          {ownPickups && (
            <Alert
              type={Alert.Type.INFO}
              message={t(
                'Your card has been approved, and we will approach you soon. Please have your ID ready to show when receiving your card.',
              )}
              hasIcon
            />
          )}
          <List>
            {!ownPickups && (
              <ListItem
                title={t('Pick up your {card name}', {
                  '{card name}': getOrganization(organizationCode),
                })}
                icon={<BoxIcon height={24} width={24} className={style.icon} />}
                actionable
                size={ListItem.Size.SMALL}
                onClick={showPickupLocations}
              />
            )}

            <ListItem
              title={t('Activate {card name}', {
                '{card name}': getOrganization(organizationCode),
              })}
              icon={
                <SwitchIcon height={24} width={24} className={style.icon} />
              }
              actionable
              size={ListItem.Size.SMALL}
              onClick={startActivationFlow}
            />
          </List>
        </>,
      )}
      {renderIf(
        [PickupType.CREDIT].includes(type),
        <List>
          <ListItem
            title={t('Pick up your {card name}', {
              '{card name}': t('Credit'),
            })}
            icon={<BoxIcon height={24} width={24} className={style.icon} />}
            actionable
            size={ListItem.Size.SMALL}
            onClick={showPickupLocations}
          />
        </List>,
      )}
      <Popup
        title={t("You haven't shared your location")}
        subtitle={t(
          'Please update your sharing settings in your phone for a better app experience',
        )}
        icon={LocationPin}
        showing={showPopup}
        primaryCta={{
          text: t('Settings'),
          onClick: () => {
            PermissionsEvent.check(
              PermissionsEvent.Resources.LOCATION,
              PermissionsEvent.Action.OPEN_SETTINGS,
            );
          },
        }}
        secondaryCta={{
          text: t('Cancel'),
          onClick: () => {
            NavigationUtils.navigate(
              NavigationUtils.Routes.PICKUP_LISTING,
              true,
              { type: type },
            );
          },
        }}
        onCloseClick={() => {
          NavigationUtils.navigate(
            NavigationUtils.Routes.PICKUP_LISTING,
            true,
            { type: type },
          );
        }}
        data-testid="location-not-shared-pop-up"
      />

      {renderIf(loadingScreen, <LoadingScreen />)}
    </main>
  );
}
